/**
 * Final expert-review submit screen — portfolio summary before sending to advisor.
 *
 * Belongs here: submit CTA and read-only recap.
 * Does not belong here: questionnaire editing.
 */
import { useEffect, useMemo } from 'react'
import { useExpertReviewNavigation } from './navigation/expertReviewNavigation'
import { getBottomPadding } from '../../../utils/sessionStorage'
import { EventNames } from '../../../types/misc.types'
import { expertReviewSafeAreaTopStyle } from './shared/expertReviewSafeArea'
import { PrimaryButton } from '../../../components/execution/PrimaryButton'
import RenderHTML from '../../../components/ui-components/RenderHTML'
import { useUserDetails } from '../../../hooks/useUserDetails'
import { useStrategyQuestionnaireContext } from './strategy-questionnaire/flow-engine/StrategyQuestionnaireProvider'
import { getAllocationRiskProfileConfig } from './strategy-questionnaire/setup/config'
import { formatRupeeLabel } from '../plans-v4/display-values/lumpsumPlanAmount'
import { ExpertReviewTopBar } from './shared/components/ExpertReviewTopBar'
import { ExpertReviewEventIds, ExpertReviewScreen } from './analytics/expertReviewEvents'
import { lightImpactVibrate, trackEvent } from '../../../utils/appBridge'

const PERSON_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
  <circle cx="12" cy="8" r="4"/>
  <path d="M4 20c0-4 4-6 8-6s8 2 8 6"/>
</svg>`

const CLOCK_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <circle cx="8" cy="8" r="6"/>
  <path d="M8 4v4l2 2"/>
</svg>`

export default function ExpertReviewSubmitPage() {
  const { goToHub, goBack } = useExpertReviewNavigation()
  const { userDetails } = useUserDetails()
  const { riskProfile } = useStrategyQuestionnaireContext()

  const riskProfileConfig = useMemo(() => getAllocationRiskProfileConfig(riskProfile ?? undefined), [riskProfile])
  const userName = userDetails?.name ?? ''
  const portfolioValue = 2434223
  const mutualFundCount = 21
  const categoryCount = 14
  const profileName = riskProfileConfig?.name ?? '—'

  const handleBack = () => goBack()

  const handleSubmit = () => {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: ExpertReviewScreen.Submit,
        eventId: ExpertReviewEventIds.Submit,
        buttonLabel: 'Submit'
      }
    })
    // TODO: wire to submit API
    goToHub()
  }

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: { ScreenName: ExpertReviewScreen.Submit }
    })
  }, [])

  return (
    <div
      className="h-screen flex flex-col bg-white"
      style={{
        ...expertReviewSafeAreaTopStyle(),
        paddingBottom: `calc(${getBottomPadding()}px + 16px)`
      }}
    >
      <header className="shrink-0 px-5 pb-3 pt-2">
        <ExpertReviewTopBar
          onBack={handleBack}
          menu="strategy"
          analyticsScreen={ExpertReviewScreen.Submit}
          testLocatorBack="allocation-review-back"
          testLocatorMenu="allocation-review-menu"
        />
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-5">
        <section className="rounded-2xl border border-[#F0F4F7] bg-[#FAFBFC] p-5 opacity-90">
          <div className="flex items-center gap-3">
            <span
              className="flex-shrink-0 w-6 h-6 text-[#6A6E6E] [&>svg]:w-full [&>svg]:h-full"
              aria-hidden
            >
              <RenderHTML innerHTMLText={PERSON_ICON_SVG} className="w-full h-full" />
            </span>
            <span className="text-[14px] font-[500] text-[#232323]">{userName}</span>
          </div>
          <div className="mt-4">
            <p className="text-[12px] font-[400] text-[#6A6E6E]">Portfolio to be reviewed</p>
            <p className="mt-0.5 text-[22px] font-[600] text-[#232323]">
              {formatRupeeLabel({ rawAmount: portfolioValue })}
            </p>
            <p className="mt-1 text-[12px] font-[400] text-[#6A6E6E]">
              {mutualFundCount} Mutual Funds • {categoryCount} Categories
            </p>
          </div>
          <div className="mt-4">
            <p className="text-[12px] font-[400] text-[#6A6E6E]">Investor profile</p>
            <p className="mt-0.5 text-[14px] font-[600] text-[#232323]">{profileName}</p>
          </div>
        </section>

        <section className="mt-8">
          <h1 className="text-[22px] font-[600] text-[#232323] leading-[130%]" test-locator="allocation-review-title">
            Submit for Expert Review
          </h1>
          <p className="mt-2 text-[14px] font-[400] text-[#6A6E6E] leading-[150%]" test-locator="allocation-review-description">
            Our in house experts analyse and review your portfolio to prepare a step by step
            allocation plan for your lump sum investments
          </p>
          <div className="mt-4 flex items-center gap-2 rounded-xl bg-[#F0F4F7] px-4 py-3 w-fit">
            <span
              className="flex-shrink-0 w-4 h-4 text-[#6A6E6E] [&>svg]:w-full [&>svg]:h-full"
              aria-hidden
            >
              <RenderHTML innerHTMLText={CLOCK_ICON_SVG} className="w-full h-full" />
            </span>
            <span className="text-[13px] font-[500] text-[#6A6E6E]">Takes up to 6 days</span>
          </div>
        </section>
      </div>

      <div className="shrink-0 px-5 pt-4">
        <PrimaryButton
          testLocator="allocation-review-submit"
          label="Submit"
          onClick={handleSubmit}
          primaryButtonClassName="w-full !h-[56px] !font-[500] !rounded-[16px]"
        />
      </div>
    </div>
  )
}
